const EVENT_LABELS = {
  replace: 'replacement',
  rotate: 'rotation',
  top_up: 'top-up',
  inspect: 'inspection',
};

function partDescription(part) {
  if (part.category === 'oil') {
    const bits = [
      part.oil_spec || part.name || 'Engine oil',
      part.quantity != null ? `${part.quantity}${part.unit || 'L'}` : null,
    ].filter(Boolean);
    return bits.join(' · ');
  }
  if (part.category === 'tire') {
    const event = EVENT_LABELS[part.event_type] || part.event_type;
    const position = part.tire_position && part.tire_position !== 'all' ? part.tire_position : null;
    const size = part.name?.trim() || null;
    return ['Tire', event, position, size].filter(Boolean).join(' · ');
  }
  if (part.category === 'battery') {
    const bits = [part.name || 'Battery', part.manufacturer, part.part_number]
      .filter((value) => value && String(value).trim());
    return bits.join(' · ') || 'Battery';
  }
  const bits = [part.name, part.manufacturer, part.part_number]
    .filter((value) => value && String(value).trim());
  return bits.join(' · ') || 'Spare part';
}

function laborDescription(part) {
  const name = part.name?.trim();
  switch (part.category) {
    case 'oil':
      return 'Oil & filter service labour';
    case 'battery':
      return name ? `Battery replacement labour — ${name}` : 'Battery replacement labour';
    case 'tire': {
      if (part.event_type === 'rotate') return 'Tire rotation labour';
      if (part.event_type === 'replace') {
        return name ? `Tire replacement labour — ${name}` : 'Tire replacement labour';
      }
      if (part.event_type === 'top_up') return null;
      if (part.event_type === 'inspect') return null;
      return 'Tire service labour';
    }
    case 'spare':
      return name ? `Fitting labour — ${name}` : 'Spare part fitting labour';
    case 'other':
      if (/labou?r/i.test(name || '') || /labou?r/i.test(part.notes || '')) {
        return null;
      }
      return name ? `${name} — labour` : 'Labour';
    default:
      return null;
  }
}

function flattenHealthNote(value) {
  return String(value || '')
    .split(/\n+/)
    .map((line) => line.trim())
    .filter(Boolean)
    .join(' · ');
}

function isNegativeHealthNote(value) {
  const text = flattenHealthNote(value).toLowerCase();
  if (!text) return true;
  return (
    text === 'no warning lights' ||
    text === 'no accident history reported' ||
    text.startsWith('no warning lights ·') ||
    text.startsWith('no accident history reported ·')
  );
}

/** Inspection findings that can become billable quote lines. History notes stay off the quote. */
function inspectionLaborLines(healthReport) {
  if (!healthReport) return [];
  const lines = [];
  const findings = flattenHealthNote(healthReport.diagnostic_findings);
  const accident = flattenHealthNote(healthReport.accident_repair_notes);

  if (findings && !isNegativeHealthNote(findings)) {
    lines.push({
      key: 'health-diagnostics',
      line_kind: 'service',
      description: `Diagnostics — ${findings}`,
      quantity: 1,
      unit_price: '',
      source: 'inspection',
    });
  }
  if (accident && !isNegativeHealthNote(accident)) {
    lines.push({
      key: 'health-accident',
      line_kind: 'service',
      description: `Body / accident repair — ${accident}`,
      quantity: 1,
      unit_price: '',
      source: 'inspection',
    });
  }
  return lines;
}

export function buildLinesFromResaleRepairs(repairs = []) {
  return (repairs || [])
    .filter((repair) => repair?.title?.trim())
    .map((repair, index) => {
      const min = Number(repair.cost_min_egp);
      const max = Number(repair.cost_max_egp);
      let unit_price = '';
      if (Number.isFinite(min) && Number.isFinite(max)) {
        unit_price = String(Math.round((min + max) / 2));
      } else if (Number.isFinite(min)) {
        unit_price = String(min);
      } else if (Number.isFinite(max)) {
        unit_price = String(max);
      }

      const priority = repair.priority === 'urgent' ? ' (urgent)' : '';
      const notes = repair.notes?.trim() ? ` — ${repair.notes.trim()}` : '';
      return {
        key: `resale-repair-${index}-${repair.title.trim()}`,
        job_card_part_id: null,
        linked_part_id: null,
        line_kind: 'service',
        description: `${repair.title.trim()}${priority}${notes}`,
        quantity: 1,
        unit_price,
        source: 'resale_inspection',
      };
    });
}

export function buildLinesFromResaleInspection(booking, report, servicePrice) {
  const serviceLabel =
    booking?.service_type?.trim() || 'Certified pre-purchase inspection';
  const vehicle = [report?.brand, report?.model, report?.plate_number]
    .filter(Boolean)
    .join(' ');
  const description = vehicle
    ? `${serviceLabel} — ${vehicle}`
    : report?.report_number
      ? `${serviceLabel} (${report.report_number})`
      : serviceLabel;
  const price =
    servicePrice != null && servicePrice !== ''
      ? String(servicePrice)
      : '';

  return [
    {
      key: 'resale-inspection-fee',
      job_card_part_id: null,
      linked_part_id: null,
      line_kind: 'service',
      description,
      quantity: 1,
      unit_price: price,
      source: 'resale_inspection_fee',
    },
  ];
}

export function buildLinesFromWorkLog(parts, booking, healthReport) {
  const sorted = [...(parts || [])].sort(
    (a, b) => new Date(a.created_at).getTime() - new Date(b.created_at).getTime(),
  );
  const lines = [];

  for (const part of sorted) {
    const qty =
      part.category === 'oil'
        ? Math.max(1, Number(part.quantity) || 1)
        : 1;
    lines.push({
      key: `part-${part.id}`,
      job_card_part_id: part.id,
      linked_part_id: null,
      line_kind: part.category === 'oil' ? 'fluid' : 'part',
      description: partDescription(part),
      quantity: qty,
      unit_price: '',
      source: 'work_log',
    });

    const labor = laborDescription(part);
    if (labor) {
      lines.push({
        key: `labor-${part.id}`,
        job_card_part_id: null,
        linked_part_id: part.id,
        line_kind: 'labor',
        description: labor,
        quantity: 1,
        unit_price: '',
        source: 'work_log',
      });
    }
  }

  if (lines.length === 0 && booking?.service_type?.trim()) {
    lines.push({
      key: 'visit-service',
      job_card_part_id: null,
      linked_part_id: null,
      line_kind: 'service',
      description: `${booking.service_type.trim()} — labour`,
      quantity: 1,
      unit_price: '',
      source: 'service',
    });
  }

  const inspectionLines = inspectionLaborLines(healthReport);
  for (const line of inspectionLines) {
    if (!lines.some((item) => item.key === line.key)) {
      lines.push(line);
    }
  }

  return lines;
}

function isImportedLine(line) {
  return Boolean(
    line.job_card_part_id ||
      line.linked_part_id ||
      line.source === 'work_log' ||
      line.source === 'service' ||
      line.source === 'inspection' ||
      line.source === 'resale_inspection' ||
      line.source === 'resale_inspection_fee',
  );
}

export function mergeQuoteDraftWithResale(currentDraft, resaleLines) {
  const manualLines = (currentDraft || []).filter(
    (line) => line.description?.trim() && !isImportedLine(line),
  );

  const preserved = new Map();
  for (const line of currentDraft || []) {
    if (line.key && (line.source === 'resale_inspection' || line.source === 'resale_inspection_fee')) {
      preserved.set(line.key, line);
    }
  }

  const merged = [...manualLines];
  for (const resaleLine of resaleLines) {
    merged.push(preserved.get(resaleLine.key) || resaleLine);
  }

  return merged.length > 0 ? merged : resaleLines;
}

export function mergeQuoteDraftWithWorkLog(currentDraft, workLogLines) {
  const manualLines = (currentDraft || []).filter(
    (line) => line.description?.trim() && !isImportedLine(line),
  );

  const preserved = new Map();
  for (const line of currentDraft || []) {
    if (line.job_card_part_id) {
      preserved.set(`part-${line.job_card_part_id}`, line);
    }
    if (line.linked_part_id) {
      preserved.set(`labor-${line.linked_part_id}`, line);
    }
    if (line.key && (line.source === 'inspection' || line.source === 'service' || line.source === 'resale_inspection_fee')) {
      preserved.set(line.key, line);
    }
  }

  const merged = [...manualLines];
  for (const workLine of workLogLines) {
    const preserveKey = workLine.job_card_part_id
      ? `part-${workLine.job_card_part_id}`
      : workLine.linked_part_id
        ? `labor-${workLine.linked_part_id}`
        : workLine.key;
    merged.push(preserved.get(preserveKey) || workLine);
  }

  return merged.length > 0 ? merged : workLogLines;
}

export function countWorkLogParts(parts) {
  return (parts || []).length;
}
